import { createPortal } from 'react-dom';
import LangEnum from '../../../../lang/LangEnum';
import { useRachelAttachmentsContext } from '../../contexts/RachelAttachments.context';
import { useRachelRegionsContext } from '../../contexts/RachelRegions.context';
import { useIsRachelAvailable } from '../../hooks/useIsRachelAvailable';
import { useRachelPresentation } from '../../hooks/useRachelPresentation';
import { useRachelSubmit } from '../../hooks/useRachelSubmit';
import { useRachelStore } from '../../store/rachel.store';
import {
  selectCloseRachel,
  selectIsRachelOpen,
  selectOpenRachel,
} from '../../store/rachel.store.selectors';
import RachelInputView from './RachelInputView';
import { useRachelContextChip } from './useRachelContextChip';
import { useRachelInput } from './useRachelInput';
import { useRachelInputRefocus } from './useRachelInputRefocus';

const RachelInputInner = () => {
  const rachelSubmit = useRachelSubmit();
  const {
    inputRef,
    value,
    setValue,
    hasText,
    isActive,
    isMultiline,
    isStreaming,
    measureRef,
    setIsHovered,
    stop,
    handleSend,
    handleKeyDown,
    handleFocus,
    handleBlur,
  } = useRachelInput({ rachelSubmit, shouldOpenOnFocus: false, shouldPublishFocus: true });
  const contextChip = useRachelContextChip();
  const presentation = useRachelPresentation();
  const isOpen = useRachelStore(selectIsRachelOpen);
  const open = useRachelStore(selectOpenRachel);
  const close = useRachelStore(selectCloseRachel);
  const { footerSlotNode } = useRachelRegionsContext();
  const { attachments, addFiles, removeAttachment, hasBlockingAttachment } =
    useRachelAttachmentsContext();
  const isOnPage = presentation.mode === 'page';
  const isPortaled = presentation.mode !== 'docked' && !!footerSlotNode;
  useRachelInputRefocus({ inputRef, portalNode: footerSlotNode });

  const togglePanel = () => {
    if (isOpen) {
      close();
      return;
    }

    open();
    inputRef.current?.focus();
  };

  const view = (
    <RachelInputView
      value={value}
      isActive={isActive || isOnPage}
      isVertical={isMultiline}
      isStreaming={isStreaming}
      hasText={hasText}
      attachments={attachments}
      hasBlockingAttachment={hasBlockingAttachment}
      contextChip={isOpen && !isOnPage ? contextChip : null}
      shouldReserveContextChipSpace={isPortaled}
      inputRef={measureRef}
      onChange={setValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onSend={handleSend}
      onStop={stop}
      sparkLabel={isOpen ? LangEnum.RACHEL_CLOSE : LangEnum.RACHEL_OPEN}
      onSparkClick={isOnPage ? undefined : togglePanel}
      onAttachFiles={addFiles}
      onRemoveAttachment={removeAttachment}
      onHoverChange={setIsHovered}
    />
  );

  if (isPortaled && footerSlotNode) {
    return createPortal(view, footerSlotNode);
  }

  return view;
};

const RachelInput = () => {
  const isAvailable = useIsRachelAvailable();
  if (!isAvailable) {
    return null;
  }
  return <RachelInputInner />;
};

export default RachelInput;
